// Settings → Systems (docs/systems.md): the list's view in the URL, the edit form of one system and what is wrong with it.
// Pure helpers; the page is pages/Systems.tsx, the window components/SystemEditor.tsx.
import type { SystemChange } from './api.ts';
import { SYSTEM_CODE, type SystemInfo, type SystemStatus } from './systems.ts';

/** The kinds of system (cloud/src/registry.ts SYSTEM_KINDS) as people say them. */
export const KIND_LABEL: Record<string, string> = {
  erp: 'ERP', crm: 'CRM', wms: 'WMS', ecommerce: 'E-commerce', finance: 'Finance', hr: 'HR', database: 'Database', api: 'API',
  saas: 'SaaS', 'file-transfer': 'File transfer', edi: 'EDI operator', dwh: 'Data warehouse', other: 'Other',
};
export const KINDS = Object.keys(KIND_LABEL);

/** The icons a system can have (cloud/src/registry.ts SYSTEM_ICONS), with what they stand for. */
export const ICON_LABEL: Record<string, string> = {
  database: 'Database', api: 'API', cloud: 'Cloud / SaaS', file: 'File', server: 'Server', cart: 'Shop', truck: 'Logistics',
  users: 'People', invoice: 'Invoice', warehouse: 'Warehouse',
};
export const ICONS = Object.keys(ICON_LABEL);

/** Colours to pick from: the hues the map draws lanes in, readable on both themes. */
export const SYSTEM_PALETTE = ['#2F4BE8', '#0369A1', '#0E7490', '#0D9488', '#0B7A55', '#16A34A', '#65A30D', '#A16207', '#C2410C', '#BE123C', '#B4237A', '#7C4DDB', '#475569'];

/** The limits the Cloud checks (cloud/src/registry.ts SYSTEM_LIMITS). */
export const SYSTEM_LIMITS = { name: 60, description: 2000, owner: 120, vendor: 120, url: 500, group: 40, aliases: 20, logoBytes: 200 * 1024 };
export const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export const STATUS_LABEL: Record<SystemStatus, string> = { new: 'New', checked: 'Checked', hidden: 'Hidden' };

// ---------------------------------------------------------------- the list

export type StatusFilter = SystemStatus | 'all';
export const STATUS_FILTERS: StatusFilter[] = ['all', 'new', 'checked', 'hidden'];
export type SystemSort = 'name' | 'code' | 'models' | 'newest';
export const SORTS: Record<SystemSort, string> = { name: 'Name', code: 'Code', models: 'Most data models', newest: 'Newest first' };

export interface SystemsView { status: StatusFilter; q: string; sort: SystemSort }

/** The view of the list from the URL (status=, q=, sort=); anything unknown is the default. */
export function readSystemsView(p: URLSearchParams): SystemsView {
  const status = p.get('status') as StatusFilter | null;
  const sort = p.get('sort') as SystemSort | null;
  return {
    status: status && STATUS_FILTERS.includes(status) ? status : 'all',
    q: p.get('q') ?? '',
    sort: sort && sort in SORTS ? sort : 'name',
  };
}

/** The URL with a change of the view; defaults leave the URL. */
export function withSystemsView(p: URLSearchParams, patch: Partial<SystemsView>): URLSearchParams {
  const v = { ...readSystemsView(p), ...patch };
  const out = new URLSearchParams(p);
  const put = (k: string, value: string, dflt: string) => { if (value && value !== dflt) out.set(k, value); else out.delete(k); };
  put('status', v.status, 'all');
  put('q', v.q, '');
  put('sort', v.sort, 'name');
  return out;
}

/** How many systems are in each status. */
export function systemCounts(list: readonly SystemInfo[]): Record<StatusFilter, number> {
  const out = { all: list.length, new: 0, checked: 0, hidden: 0 };
  for (const s of list) out[s.status]++;
  return out;
}

/** The systems as listed: of the status asked for, matching the text (name, code, alias, kind, vendor, owner, data model), sorted; hidden ones last. */
export function listSystems(list: readonly SystemInfo[], v: SystemsView, modelsOf: (s: SystemInfo) => readonly string[] = (s) => s.classes): SystemInfo[] {
  const q = v.q.trim().toLowerCase();
  const hit = (s: SystemInfo) => !q || [s.name, s.code, ...s.aliases, s.kind ? KIND_LABEL[s.kind] ?? s.kind : '', s.vendor ?? '', s.owner ?? '', ...modelsOf(s)]
    .some((x) => x.toLowerCase().includes(q));
  const by: Record<SystemSort, (a: SystemInfo, b: SystemInfo) => number> = {
    name: (a, b) => a.name.localeCompare(b.name) || a.code.localeCompare(b.code),
    code: (a, b) => a.code.localeCompare(b.code),
    models: (a, b) => modelsOf(b).length - modelsOf(a).length || a.name.localeCompare(b.name),
    newest: (a, b) => b.firstSeenAt - a.firstSeenAt || a.name.localeCompare(b.name),
  };
  const hidden = (s: SystemInfo) => (s.status === 'hidden' ? 1 : 0);
  return list.filter((s) => (v.status === 'all' || s.status === v.status) && hit(s)).sort((a, b) => hidden(a) - hidden(b) || by[v.sort](a, b));
}

// ---------------------------------------------------------------- the edit form

export interface SystemForm {
  name: string; code: string; kind: string; icon: string; color: string; group: string; description: string; owner: string; vendor: string; url: string;
  aliases: string[]; aliasText: string; markChecked: boolean;
}

/** The form of a system (none: a new one). */
export function formOf(s?: SystemInfo): SystemForm {
  return {
    name: s?.name ?? '', code: s?.code ?? '', kind: s?.kind ?? '', icon: s?.icon ?? '', color: s?.color ?? '', group: s?.group ?? '', description: s?.description ?? '',
    owner: s?.owner ?? '', vendor: s?.vendor ?? '', url: s?.url ?? '', aliases: s?.aliases ?? [], aliasText: '', markChecked: s?.status === 'new',
  };
}

/** A typed code as the Cloud keeps it (trimmed, upper case), or null when it is not one. */
export function codeOf(text: string): string | null {
  const t = text.trim();
  return SYSTEM_CODE.test(t) ? t.toUpperCase() : null;
}

/** Aliases typed as text (commas or spaces between them): the valid new ones added, the invalid ones back as text. */
export function addAliases(text: string, existing: readonly string[], own: string): { aliases: string[]; rest: string } {
  const out = [...existing];
  const bad: string[] = [];
  for (const raw of text.split(/[\s,]+/).filter(Boolean)) {
    const c = codeOf(raw);
    if (!c) bad.push(raw);
    else if (c !== own.toUpperCase() && !out.includes(c)) out.push(c);
  }
  return { aliases: out.slice(0, SYSTEM_LIMITS.aliases), rest: bad.join(' ') };
}

export type FormField = 'name' | 'code' | 'url' | 'aliases' | 'description' | 'owner' | 'vendor' | 'color' | 'group';

/**
 * What is wrong with the form, by field. `taken`: codes and aliases of the other systems (upper case) — a new code must
 * not be one; an alias may be one only when that system is still new (it is merged), which the Cloud decides.
 */
export function formProblems(f: SystemForm, editingCode: boolean, taken: ReadonlySet<string> = new Set()): Partial<Record<FormField, string>> {
  const out: Partial<Record<FormField, string>> = {};
  const L = SYSTEM_LIMITS;
  if (!f.name.trim()) out.name = 'A system needs a name';
  else if (f.name.trim().length > L.name) out.name = `At most ${L.name} characters`;
  if (editingCode) {
    const c = codeOf(f.code);
    if (!c) out.code = '1 to 24 letters, digits, _ . or -';
    else if (taken.has(c)) out.code = `${c} is already a system or an alias`;
  }
  const u = f.url.trim();
  if (u) {
    let ok = false;
    try { ok = ['http:', 'https:'].includes(new URL(u).protocol); } catch { ok = false; }
    if (!ok) out.url = 'An address starting with https:// or http://';
    else if (u.length > L.url) out.url = `At most ${L.url} characters`;
  }
  if (f.aliasText.trim() && addAliases(f.aliasText, f.aliases, f.code).rest) out.aliases = 'An alias is a code: 1 to 24 letters, digits, _ . or -';
  else if (f.aliases.length > L.aliases) out.aliases = `At most ${L.aliases} aliases`;
  if (f.description.trim().length > L.description) out.description = `At most ${L.description} characters`;
  if (f.owner.trim().length > L.owner) out.owner = `At most ${L.owner} characters`;
  if (f.vendor.trim().length > L.vendor) out.vendor = `At most ${L.vendor} characters`;
  if (f.group.trim().length > L.group) out.group = `At most ${L.group} characters`;
  if (f.color && !/^#[0-9a-fA-F]{6}$/.test(f.color)) out.color = 'A colour is #rrggbb';
  return out;
}

/** The PATCH (or POST) body of a form: every field (empty clears it), aliases with what is still typed, the code when it may change. */
export function patchOf(f: SystemForm, s: SystemInfo | undefined): SystemChange & { name: string } {
  const own = codeOf(f.code) ?? s?.code ?? '';
  const out: SystemChange & { name: string } = {
    name: f.name.trim(), kind: f.kind || null, icon: f.icon || null, color: f.color ? f.color.toUpperCase() : null, group: f.group.trim() || null,
    description: f.description.trim() || null, owner: f.owner.trim() || null, vendor: f.vendor.trim() || null, url: f.url.trim() || null,
    aliases: addAliases(f.aliasText, f.aliases, own).aliases,
  };
  if (!s || s.source === 'manual') out.code = own;
  if (s && f.markChecked && s.status === 'new') out.status = 'checked';
  return out;
}

/** Why a picked logo file cannot be used, or null. */
export function logoFileProblem(file: { type: string; size: number }): string | null {
  if (!LOGO_TYPES.includes(file.type)) return 'A logo must be PNG, JPEG or WebP (no SVG)';
  if (file.size > SYSTEM_LIMITS.logoBytes) return 'A logo can be at most 200 KB';
  if (file.size === 0) return 'The file is empty';
  return null;
}
